import { useEffect, useState } from "react";
import api from "../lib/api";
import { Trophy, CheckCircle2, BarChart3 } from "lucide-react";

const COLOR = { sky: "bg-sky-500", amber: "bg-amber-500", emerald: "bg-emerald-500", rose: "bg-rose-500" };

export default function Progress() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/progress").then(r => setP(r.data)); }, []);
  if (!p) return <div className="p-12 text-center text-slate-500">Loading…</div>;

  return (
    <div data-testid="progress-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500 mb-2"><BarChart3 size={14} className="inline -mt-1 mr-1" strokeWidth={3}/> Your progress</div>
        <h1 className="text-5xl font-black text-slate-900 tracking-tight">Look how far you've come</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <Big label="Lessons completed" value={p.completed} sub={`of ${p.total_lessons}`} icon={CheckCircle2} color="emerald" />
        <Big label="Average score" value={`${p.average_score}%`} sub="across all quizzes" icon={Trophy} color="amber" />
        <Big label="Total lessons" value={p.total_lessons} sub="VA SOL aligned" icon={BarChart3} color="sky" />
      </div>

      <h2 className="text-2xl font-black text-slate-900 mb-4">By subject</h2>
      <div className="grid md:grid-cols-2 gap-4 mb-12">
        {Object.entries(p.by_subject).map(([k, s]) => {
          const pct = s.total ? Math.round(s.completed / s.total * 100) : 0;
          return (
            <div key={k} data-testid={`subject-progress-${k}`} className="bg-white rounded-3xl p-6 border border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <div className="font-black text-lg text-slate-900">{s.name}</div>
                <div className="text-sm font-bold text-slate-500">{s.completed}/{s.total}</div>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${COLOR[s.color] || "bg-sky-500"}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      <h2 className="text-2xl font-black text-slate-900 mb-4">Recent quizzes</h2>
      <div className="bg-white rounded-3xl border border-slate-100 divide-y divide-slate-100">
        {p.recent.length === 0 && <div className="p-6 text-center text-slate-400">No quizzes yet. Start a lesson!</div>}
        {p.recent.map((r) => (
          <div key={r.lesson_id} className="p-5 flex items-center justify-between">
            <div>
              <div className="font-bold text-slate-900">{r.lesson_id}</div>
              <div className="text-xs text-slate-500">{new Date(r.completed_at).toLocaleString()}</div>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-black ${r.passed ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
              {r.percent}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Big({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100">
      <div className={`w-12 h-12 rounded-2xl bg-${color}-100 flex items-center justify-center mb-4`}>
        <Icon className={`text-${color}-600`} strokeWidth={2.5}/>
      </div>
      <div className="text-xs font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-4xl font-black text-slate-900 mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}
